import { ChangeEvent, useEffect, useState } from 'react'
import {
  FileUploadPassthroughProps,
  FileUploadReturnType,
  ProcessedFileFormats
} from './FileUpload'
import { blobToBase64 } from '../../utils/fileUtils'

type UseFileUploadReturn = {
  fileName: string
  errorMessage: string
  handleFileSelection: (e: ChangeEvent<HTMLInputElement>) => void
  clearFileSelection: () => void
}

export const useFileUpload = ({
  onChange,
  accept: a,
  maxFileSizeMB = 10,
  returnType = FileUploadReturnType.FILE,
  onError
}: FileUploadPassthroughProps): UseFileUploadReturn => {
  const accept = a?.replaceAll(' ', '') ?? 'image/png,image/jpeg'

  const [fileToProcess, setFileToProcess] = useState<File | undefined>(undefined)
  const [fileName, setFileName] = useState('')
  const [errorMessage, setEM] = useState('')
  const [processedFile, setProcessedFile] = useState<ProcessedFileFormats | undefined>(undefined)

  const setErrorMessage = (v: string) => {
    setEM(v)
    onError?.()
  }

  const rejectFileSelection = (
    e: ChangeEvent<HTMLInputElement>,
    newErrorMessage?: string
  ): void => {
    setFileToProcess(undefined)
    setProcessedFile(undefined)
    setFileName('')
    e.target.value = ''
    if (newErrorMessage) setErrorMessage(newErrorMessage)
  }

  const handleFileSelection = (e: ChangeEvent<HTMLInputElement>): void => {
    setErrorMessage('')
    const file = e?.target?.files?.[0]

    if (!file) return rejectFileSelection(e, '')

    const { name, type, size } = file ?? {}
    const isCompleteFile = name && type && size

    if (!isCompleteFile)
      return rejectFileSelection(e, 'File is missing required properties: name, type, size')

    const isAcceptable = (() => {
      const acceptableTypes = accept.split(',')

      return acceptableTypes.some(a => {
        if (a.includes('/*')) {
          return a.split('/')[0] === type.split('/')[0]
        }
        return a === type
      })
    })()

    if (!isAcceptable)
      return rejectFileSelection(
        e,
        `File type: ${type} is not included in the accepted list of types: ${accept.replaceAll(',', ', ')}`
      )

    const BYTES_PER_MB = 1000000
    const selectedFileSizeInMB = size ? size / BYTES_PER_MB : 0
    const roundedFileSizeInMB = Math.round(selectedFileSizeInMB * 10) / 10
    const isWithinSizeLimits = selectedFileSizeInMB <= maxFileSizeMB

    if (!isWithinSizeLimits)
      return rejectFileSelection(
        e,
        `File size ${roundedFileSizeInMB}MB is greater than the max file size: ${maxFileSizeMB}MB`
      )
    else {
      setFileToProcess(file)
      setFileName(name)
    }
  }

  const clearFileSelection = (): void => {
    setFileToProcess(undefined)
    setFileName('')
    setErrorMessage('')
    setProcessedFile(undefined)
    if (onChange) onChange('')
  }

  useEffect(() => {
    if (fileToProcess && !processedFile) {
      switch (returnType) {
        case FileUploadReturnType.FILE:
          setProcessedFile(fileToProcess)
          break
        case FileUploadReturnType.OBJECT_URL:
          const url = URL.createObjectURL(fileToProcess)
          setProcessedFile(url)
          break
        case FileUploadReturnType.BASE64:
          blobToBase64(fileToProcess).then(res => setProcessedFile(res))
          break
      }
    }
  }, [fileToProcess, processedFile, returnType])

  useEffect(() => {
    if (processedFile && onChange) {
      onChange(processedFile)
      setProcessedFile(undefined)
      setFileToProcess(undefined)
    }
  }, [onChange, processedFile])

  return { fileName, errorMessage, handleFileSelection, clearFileSelection }
}
